import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Brand, TopControls } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2, CalendarClock, Gift, ShieldCheck } from "lucide-react";
import { money, vatInfo, annualSaving } from "@/lib/pricing";

export default function CompleteSubscription() {
  const { lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const sessionId = sp.get("session_id");
  const canceled = sp.get("canceled");

  const [info, setInfo] = useState(null);
  const [interval, setInterval2] = useState("month");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activated, setActivated] = useState(false);
  const [polling, setPolling] = useState(!!sessionId);

  const loadInfo = useCallback(async () => {
    try { const { data } = await api.get(`/subscription/complete/${token}`); setInfo(data); if (data.already_active) setActivated(true); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) loadInfo(); else setLoading(false); }, [token, loadInfo]);

  // Poll payment status after returning from Stripe.
  useEffect(() => {
    if (!sessionId || !token || activated) return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/subscription/complete/${token}/status`, { params: { session_id: sessionId } });
        if (data.activated) { setActivated(true); setPolling(false); clearInterval(t); }
      } catch {}
      if (tries > 30) { setPolling(false); clearInterval(t); }
    }, 2000);
    return () => clearInterval(t);
  }, [sessionId, token, activated]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/subscription/complete/${token}/checkout`, { interval, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); setBusy(false); }
  };

  const plan = info?.plan;
  const trial = plan?.trial_enabled && plan?.trial_days > 0;
  const price = interval === "year" ? plan?.price_annual : plan?.price_monthly;
  const firstCharge = trial
    ? new Date(Date.now() + plan.trial_days * 86400000).toLocaleDateString("it-IT")
    : L("oggi", "today");
  const vat = plan ? vatInfo(plan, price, lang) : null;
  const saving = plan ? annualSaving(plan) : null;

  return (
    <div className="min-h-screen px-6 sm:px-10 py-10 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <Brand onClick={() => nav("/")} /><TopControls />
      </div>

      {loading ? (
        <div className="py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : !token || !info ? (
        <div className="text-center py-20" data-testid="cs-invalid">
          <h1 className="text-2xl font-bold mb-2">{L("Link non valido", "Invalid link")}</h1>
          <p className="text-muted-foreground mb-6">{L("Il link per completare l'abbonamento non è valido o è scaduto.", "The subscription link is invalid or expired.")}</p>
          <Link to="/login"><Button>{L("Vai al login", "Go to login")}</Button></Link>
        </div>
      ) : activated ? (
        <div className="text-center py-16" data-testid="cs-activated">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{L("Abbonamento attivo!", "Subscription active!")}</h1>
          <p className="text-muted-foreground max-w-md mx-auto mb-6">
            {L("Il tuo account è stato creato. Ti abbiamo inviato un'email per impostare la password e accedere a Revenue AI.",
               "Your account has been created. We emailed you a link to set your password and access Revenue AI.")}
          </p>
          <Link to="/login"><Button data-testid="cs-go-login">{L("Vai al login", "Go to login")}</Button></Link>
        </div>
      ) : polling ? (
        <div className="text-center py-24" data-testid="cs-polling">
          <Loader2 className="w-8 h-8 animate-spin inline text-primary mb-4" />
          <p className="text-muted-foreground">{L("Verifica del pagamento in corso…", "Verifying your payment…")}</p>
        </div>
      ) : (
        <div data-testid="cs-checkout">
          <h1 className="text-3xl font-bold mb-1">{L("Completa l'abbonamento", "Complete your subscription")}</h1>
          <p className="text-muted-foreground mb-6">
            {L("Richiesta", "Request")} <span className="font-mono">{info.request.request_ref}</span> · {info.request.structure_name}
          </p>
          {canceled && <div className="mb-4 text-sm rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-4 py-2">{L("Pagamento annullato. Puoi riprovare.", "Payment canceled. You can try again.")}</div>}

          <div className="rounded-2xl border border-border p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="font-semibold text-lg">{plan.name}</span>
              <div className="inline-flex rounded-full bg-muted p-1 text-sm">
                <button data-testid="cs-interval-month" onClick={() => setInterval2("month")}
                  className={`px-3 py-1 rounded-full transition-colors ${interval === "month" ? "bg-background shadow font-medium" : "text-muted-foreground"}`}>{L("Mensile", "Monthly")}</button>
                <button data-testid="cs-interval-year" onClick={() => setInterval2("year")}
                  className={`px-3 py-1 rounded-full transition-colors ${interval === "year" ? "bg-background shadow font-medium" : "text-muted-foreground"}`}>
                  {L("Annuale", "Annual")}{saving ? ` −${saving.perMonth.toFixed(2).replace(".", ",")}€/${L("mese", "mo")}` : ""}
                </button>
              </div>
            </div>
            <div className="flex items-end gap-1 mb-1">
              <span className="text-4xl font-bold" data-testid="cs-price">{money(price, plan.currency)}</span>
              <span className="text-muted-foreground mb-1">/{interval === "year" ? L("anno", "year") : L("mese", "month")}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4" data-testid="cs-vat-note">
              {vat && vat.mode === "plus"
                ? `${vat.label} · ${L("totale", "total")} ${money(vat.total, plan.currency)}`
                : vat?.label}
              {interval === "year" && saving ? ` · ${L("equivale a", "equals")} ${money(saving.monthlyEquivalent, plan.currency)}/${L("mese", "month")}` : ""}
            </p>
            <ul className="space-y-2.5 text-sm">
              {trial && (
                <li className="flex items-center gap-2 text-emerald-700"><Gift className="w-4 h-4" />
                  {L(`Prova gratuita di ${plan.trial_days} giorni — nessun addebito oggi`, `${plan.trial_days}-day free trial — no charge today`)}</li>
              )}
              <li className="flex items-center gap-2 text-muted-foreground"><CalendarClock className="w-4 h-4" />
                {L("Primo addebito", "First charge")}: <span className="font-medium text-foreground">{firstCharge}</span>
                {" "}({money(vat ? vat.total : price, plan.currency)})</li>
              <li className="flex items-center gap-2 text-muted-foreground"><ShieldCheck className="w-4 h-4" />
                {L("Rinnovo automatico gestito da Stripe · disdici quando vuoi", "Auto-renewal handled by Stripe · cancel anytime")}</li>
            </ul>
          </div>

          <Button className="w-full h-12 text-base" onClick={startCheckout} disabled={busy} data-testid="cs-pay">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : trial
              ? L("Attiva la prova e inserisci la carta", "Start trial & add card")
              : L("Vai al pagamento", "Proceed to payment")}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-3">
            {L("Pagamento sicuro tramite Stripe. Nessun dato della carta transita da Revenue AI.",
               "Secure payment via Stripe. No card data passes through Revenue AI.")}
          </p>
        </div>
      )}
    </div>
  );
}
